import React, { useState, useEffect } from 'lib/react';
import {
  EVGrid, EVSelect, EVMenuItem, EVKeyboardArrowDown,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';
import clsx from 'lib/clsx';
import LabelledCheckbox from 'commonComponents/labelled-checkbox';
import { PRODUCT_IDS } from 'constants/AppConstants';
import { useSelector, useDispatch } from 'react-redux';
import { updateInputFieldValue } from 'features/orderPreferences/OrderPreferences.actions';
import { getPropertyValues } from 'features/orderPreferences/OrderPreferences.utils';
import {
  PRODUCT_SELECTION_OTHER_IDS, PRODUCT_SELECTION_WALLS_IDS, // PRODUCT_SELECTION_FULL_HOUSE_IDS, isRoofProduct,
  getWallsPreferenceByProductId,
} from '../ProductSelection.utils';
import ProductSelectionStyles from '../ProductSelectionStyles';

function ProductSelectionElement(props) {
  const { t } = useTranslation();
  const classes = ProductSelectionStyles();
  const dispatch = useDispatch();
  const orderPreferences = useSelector(({ userData }) => userData.orderPreferences);
  const inputFieldValues = useSelector(({ orderPreference }) => orderPreference.inputFieldValues);
  const {
    products,
  } = props;

  const roofProduct = getPropertyValues(orderPreferences, inputFieldValues, 'roofProduct');
  const roofDeliveryProduct = getPropertyValues(orderPreferences, inputFieldValues, 'roofDeliveryProduct');
  const wallsAddOnProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wallsAddOnProduct');
  const deliveryAllowSubs = getPropertyValues(orderPreferences, inputFieldValues, 'deliveryAllowSubs');
  const wallsDeliveryProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wallsDeliveryProduct');
  const wallsLiteDeliveryProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wallsLiteDeliveryProduct');
  const resWallWinDoorDeliveryProduct = getPropertyValues(orderPreferences, inputFieldValues, 'resWallWinDoorDeliveryProduct');
  const resWallsFKALiteDeliveryProduct = getPropertyValues(orderPreferences, inputFieldValues, 'resWallsFKALiteDeliveryProduct');
  const wholeHomeDeliveryProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wholeHomeDeliveryProduct');
  const wallsLiteProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wallsLiteProduct');
  const resWallWinDoorProduct = getPropertyValues(orderPreferences, inputFieldValues, 'resWallWinDoorProduct');
  const resWallsFKALiteProduct = getPropertyValues(orderPreferences, inputFieldValues, 'resWallsFKALiteProduct');
  const wholeHomeProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wholeHomeProduct');
  const riskManagementDeliveryProduct = getPropertyValues(orderPreferences, inputFieldValues, 'riskManagementDeliveryProduct');
  const riskManagementProduct = getPropertyValues(orderPreferences, inputFieldValues, 'riskManagementProduct');
  const estimateDeliveryProduct = getPropertyValues(orderPreferences, inputFieldValues, 'estimateDeliveryProduct');
  const estimateProduct = getPropertyValues(orderPreferences, inputFieldValues, 'estimateProduct');
  const structureType = getPropertyValues(orderPreferences, inputFieldValues, 'structureType');
  const wallWinDoorProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wallWinDoorProduct');
  const wallsFKALiteProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wallsFKALiteProduct');

  const [selectedWallsProduct, setSelectedWallsProduct] = useState(
    wallsAddOnProduct?.value || wallsLiteProduct?.value
    || resWallWinDoorProduct?.value || resWallsFKALiteProduct?.value || wallWinDoorProduct?.value || wallsFKALiteProduct?.value || null,
  );

  const wallsAddOnCompatibilityCheck = () => {
    if ([PRODUCT_IDS.QuickSquaresResidential,
      PRODUCT_IDS.QuickSquaresCommercial,
      PRODUCT_IDS.GutterResidential,
      PRODUCT_IDS.GutterCommercial,
      PRODUCT_IDS.StandardResidential,
      PRODUCT_IDS.DiagramResidential,
      PRODUCT_IDS.DiagramCommercial,
      PRODUCT_IDS.BidPerfect,
      PRODUCT_IDS.BidPerfectCommercial,
    ]
      .includes(roofProduct.value)
    || (products?.wholeHome && [PRODUCT_IDS.WholeHome].includes(wholeHomeProduct.value))) {
      return true;
    }
    return false;
  };

  const filteredRoofAndSolarProducts = [...(products.roof || []), ...(products.solar || [])];
  const filterdWallsProducts = products.walls?.filter((product) => PRODUCT_SELECTION_WALLS_IDS.includes(product.ProductID));
  const filterdOthersProducts = products.others?.filter((product) => PRODUCT_SELECTION_OTHER_IDS.includes(product.ProductID));
  const filterdFullHouseProducts = products.wholeHome;

  useEffect(() => {
    if (wallsAddOnCompatibilityCheck()) {
      dispatch(updateInputFieldValue('wallsAddOnProduct', 'value', null));
      dispatch(updateInputFieldValue('wallsLiteProduct', 'value', null));
      dispatch(updateInputFieldValue('resWallWinDoorProduct', 'value', null));
      dispatch(updateInputFieldValue('resWallsFKALiteProduct', 'value', null));
      dispatch(updateInputFieldValue('wallWinDoorProduct', 'value', null));
      dispatch(updateInputFieldValue('wallsFKALiteProduct', 'value', null));
    }
  }, [roofProduct?.value, wholeHomeProduct?.value]);

  const handleWallsSelect = (name, value) => {
    if (selectedWallsProduct) {
      const wallProductName = getWallsPreferenceByProductId(selectedWallsProduct);
      dispatch(updateInputFieldValue(wallProductName, 'value', null));
    }
    setSelectedWallsProduct(Number(value));
  };

  const handleCheckBoxChange = (e) => {
    const { target: { name, value } } = e;
    if (name === 'roofProduct') dispatch(updateInputFieldValue('wholeHomeProduct', 'value', null));
    if (name === 'wholeHomeProduct') dispatch(updateInputFieldValue('roofProduct', 'value', null));
    if (name.toLowerCase().includes('wall')) handleWallsSelect(name, value);
    if (getPropertyValues(orderPreferences, inputFieldValues, name)?.value !== null) {
      dispatch(updateInputFieldValue(name, 'value', null));
    } else {
      dispatch(updateInputFieldValue(name, 'value', Number(value)));
    }
  };

  const handleDeliverySubAccountsCheckBoxChange = (e) => {
    const { target: { name, checked } } = e;
    dispatch(updateInputFieldValue(name, 'value', checked));
  };

  const allowedDeliveryModes = (currentProduct, allProducts) => (
    allProducts?.find((product) => (product.ProductID === currentProduct))?.WebDeliveryProducts
  || filteredRoofAndSolarProducts[0]?.WebDeliveryProducts);

  const handleChange = (e) => {
    const { target: { name, value } } = e;
    if (name === 'roofProduct') {
      const deliveryMethod = (allowedDeliveryModes(value, filteredRoofAndSolarProducts))?.[0]?.ProductID;
      dispatch(updateInputFieldValue('roofDeliveryProduct', 'value', deliveryMethod));
    }
    dispatch(updateInputFieldValue(name, 'value', value));
  };

  const isRoofProductDisabled = roofProduct?.value === null
  || !filteredRoofAndSolarProducts.some((product) => product.ProductID === roofProduct?.value
  || ((structureType.value === 1) && product.ProductID === PRODUCT_IDS.ECPremiumResidential)
  || ((structureType.value === 2) && product.ProductID === PRODUCT_IDS.ECPremiumCommercial))
  || !deliveryAllowSubs?.canEdit;

  const roofAndSolarRow = (preference, deliveryPreference, categoryProducts) => (
    <EVGrid className={classes.productSelectionElementSubContainer}>
      <EVGrid className={classes.productSelectionElementSwitchContainer}>
        <LabelledCheckbox
          checkboxId={preference}
          checkBoxName={preference}
          handleCheckboxSelectChange={handleCheckBoxChange}
          checked={roofProduct?.value !== null}
          disabled={!roofProduct?.canEdit || !categoryProducts.length}
          checkboxValue={roofProduct?.value || categoryProducts[0]?.ProductID}
          checkboxLabel={t('order_preferences__product_selection_roof_solar')}
          className={classes.deliveryAllowSubsCheckbox}
          alignLabelCenter
        />
      </EVGrid>
      <EVSelect
        name={preference}
        disabled={roofProduct?.value === null}
        IconComponent={EVKeyboardArrowDown}
        variant="outlined"
        className={classes.dropdownBox}
        value={categoryProducts.some((product) => product.ProductID === roofProduct?.value) ? roofProduct?.value : categoryProducts[0]?.ProductID}
        onChange={handleChange}
        inputProps={{ classes: { root: clsx(classes.inputTextField, 'select') } }}
      >
        {/* #genai stable key from the product id */}
        {categoryProducts.map((product) => <EVMenuItem key={product.ProductID} value={product.ProductID}>{product.Description}</EVMenuItem>)}
      </EVSelect>

      <EVSelect
        name={deliveryPreference}
        IconComponent={EVKeyboardArrowDown}
        disabled={isRoofProductDisabled}
        variant="outlined"
        value={allowedDeliveryModes(roofProduct?.value, categoryProducts)?.some((delivery) => delivery.ProductID === roofDeliveryProduct?.value)
          ? roofDeliveryProduct?.value : allowedDeliveryModes(roofProduct?.value, categoryProducts)?.[0]?.ProductID}
        className={classes.dropdownBox}
        onChange={handleChange}
        inputProps={{ classes: { root: clsx(classes.inputTextField, 'select') } }}
      >
        {/* #genai stable key from the delivery product id */}
        {allowedDeliveryModes(roofProduct?.value, categoryProducts)
          ?.map((deliverProduct) => <EVMenuItem key={deliverProduct.ProductID} value={deliverProduct.ProductID}>{deliverProduct.Name}</EVMenuItem>)}
      </EVSelect>
    </EVGrid>
  );

  const productSelectionRows = (preference, deliveryPreference, product, preferenceProduct, preferenceDeliveryProduct) => (
    <EVGrid key={product.ProductID} className={classes.productSelectionElementSubContainer}>
      <EVGrid className={classes.productSelectionElementSwitchContainer}>
        <LabelledCheckbox
          checkBoxName={preference}
          checkboxId={preference}
          handleCheckboxSelectChange={handleCheckBoxChange}
          checked={preferenceProduct?.value !== null && !(preference.toLowerCase().includes('wall') && wallsAddOnCompatibilityCheck())}
          checkboxValue={product.ProductID}
          disabled={!preferenceProduct?.canEdit || (preference.toLowerCase().includes('wall') && wallsAddOnCompatibilityCheck())}
          checkboxLabel={product.Description}
          className={classes.deliveryAllowSubsCheckbox}
          alignLabelCenter
        />
      </EVGrid>
      <EVSelect
        name={deliveryPreference}
        IconComponent={EVKeyboardArrowDown}
        variant="outlined"
        value={preferenceDeliveryProduct?.value || product?.WebDeliveryProducts[0].ProductID}
        disabled={preferenceProduct?.value === null || !deliveryAllowSubs?.canEdit
          || (preference.toLowerCase().includes('wall') && wallsAddOnCompatibilityCheck())}
        className={classes.dropdownBox}
        onChange={handleChange}
        inputProps={{ classes: { root: clsx(classes.inputTextField, 'select') } }}
      >
        {/* #genai optional chaining plus a stable key from the delivery product id */}
        {product?.WebDeliveryProducts.map((deliverProduct) => (
          <EVMenuItem key={deliverProduct.ProductID} value={deliverProduct.ProductID}>{deliverProduct.Name}</EVMenuItem>
        ))}
      </EVSelect>
    </EVGrid>
  );

  return (
    <>
      {roofAndSolarRow('roofProduct', 'roofDeliveryProduct', filteredRoofAndSolarProducts)}
      {filterdWallsProducts?.map((product) => {
        if (product.ProductID === PRODUCT_IDS.PremiumResidentialWallonly || product.ProductID === PRODUCT_IDS.PremiumCommercialWallonly) {
          return productSelectionRows('wallsAddOnProduct', 'wallsDeliveryProduct', product, wallsAddOnProduct, wallsDeliveryProduct);
        }
        if (product.ProductID === PRODUCT_IDS.WallsLite) {
          return productSelectionRows('wallsLiteProduct', 'wallsLiteDeliveryProduct', product, wallsLiteProduct, wallsLiteDeliveryProduct);
        }
        if ([PRODUCT_IDS.ResWallWinDoor, PRODUCT_IDS.WallWinDoor].includes(product.ProductID)) {
          return productSelectionRows(
            'resWallWinDoorProduct',
            'resWallWinDoorDeliveryProduct',
            product,
            resWallWinDoorProduct,
            resWallWinDoorDeliveryProduct,
          );
        }
        if ([PRODUCT_IDS.ResWallsFKALite, PRODUCT_IDS.WallsFKALite].includes(product.ProductID)) {
          return productSelectionRows(
            'resWallsFKALiteProduct',
            'resWallsFKALiteDeliveryProduct',
            product,
            resWallsFKALiteProduct,
            resWallsFKALiteDeliveryProduct,
          );
        }
        return null;
      })}
      {filterdOthersProducts?.map((product) => {
        if (product.ProductID === PRODUCT_IDS.RiskManagementResidential || product.ProductID === PRODUCT_IDS.RiskManagementCommercial) {
          return productSelectionRows(
            'riskManagementProduct',
            'riskManagementDeliveryProduct',
            product,
            riskManagementProduct,
            riskManagementDeliveryProduct,
          );
        }
        if (product.ProductID === PRODUCT_IDS.Estimate || product.ProductID === PRODUCT_IDS.CarrierEstimate) {
          return productSelectionRows('estimateProduct', 'estimateDeliveryProduct', product, estimateProduct, estimateDeliveryProduct);
        }
        return null;
      })}
      {filterdFullHouseProducts?.map((product) => {
        if (product.ProductID === PRODUCT_IDS.WholeHome || product.ProductID === PRODUCT_IDS.Full_House) {
          return productSelectionRows('wholeHomeProduct', 'wholeHomeDeliveryProduct', product, wholeHomeProduct, wholeHomeDeliveryProduct);
        }
        return null;
      })}

      <EVGrid className={clsx(classes.productsAndDeliveryElementsBottom, deliveryAllowSubs?.visible === false && 'hide')}>
        <LabelledCheckbox
          checkBoxName="deliveryAllowSubs"
          checkboxId="deliveryAllowSubs"
          handleCheckboxSelectChange={handleDeliverySubAccountsCheckBoxChange}
          value={deliveryAllowSubs?.value}
          checked={deliveryAllowSubs?.value}
          disabled={!deliveryAllowSubs?.canEdit}
          checkboxLabel={t('order_preferences__product_selection_delivery_CB')}
          className={classes.deliveryAllowSubsCheckbox}
        />
      </EVGrid>
    </>
  );
}
ProductSelectionElement.propTypes = {
  products: PropTypes.shape([PropTypes.array]).isRequired,
};
export default ProductSelectionElement;
